import { forwardRef, ReactNode, Ref } from 'react';

// material-ui
import MuiChip from '@mui/material/Chip';
import { styled, useTheme, SxProps, Theme } from '@mui/material/styles';
import { ChipProps as MuiChipProps } from '@mui/material';

// project imports
import getColors from 'utils/getColors';

// types
import { ColorProps } from 'types/extended';

// ==============================|| TYPES ||============================== //

export type ChipVariant = 'filled' | 'outlined';
export type ChipSize = 'small' | 'medium' | 'large';

// ==============================|| STYLED - CHIP ||============================== //

interface StyledChipProps {
  theme: Theme;
  chipVariant?: ChipVariant;
  chipColor?: ColorProps;
  customColor?: string;
  customTextColor?: string;
  chipSize?: ChipSize;
  customFontSize?: string | number;
  customFontWeight?: string | number;
}

const StyledChip = styled(MuiChip, {
  shouldForwardProp: (prop) =>
    prop !== 'chipVariant' &&
    prop !== 'chipColor' &&
    prop !== 'customColor' &&
    prop !== 'customTextColor' &&
    prop !== 'chipSize' &&
    prop !== 'customFontSize' &&
    prop !== 'customFontWeight'
})<StyledChipProps>(
  ({
    theme,
    chipVariant = 'filled',
    chipColor = 'primary',
    customColor,
    customTextColor,
    chipSize = 'medium',
    customFontSize,
    customFontWeight
  }) => {
    // Get colors from theme
    const colors = getColors(theme, chipColor);
    const backgroundColor = customColor || colors.main;
    const textColor = customTextColor || colors.contrastText;

    // Size configurations
    const sizeConfig = {
      small: {
        height: 20,
        fontSize: '0.75rem',
        padding: '0 8px'
      },
      medium: {
        height: 24,
        fontSize: '0.875rem',
        padding: '0 12px'
      },
      large: {
        height: 28,
        fontSize: '1rem',
        padding: '0 16px'
      }
    };

    const currentSize = sizeConfig[chipSize];

    return {
      height: `${currentSize.height}px`,
      minHeight: `${currentSize.height}px`,
      maxHeight: `${currentSize.height}px`,
      fontSize: customFontSize || currentSize.fontSize,
      padding: currentSize.padding,
      fontWeight: customFontWeight || 500,
      borderRadius: '5px',
      lineHeight: 1,
      display: 'inline-flex',
      alignItems: 'center',
      boxSizing: 'border-box',

      // Override MUI's default label padding
      '& .MuiChip-label': {
        padding: 0,
        paddingLeft: 0,
        paddingRight: 0,
        lineHeight: 1,
        display: 'flex',
        alignItems: 'center',
        height: '100%'
      },

      // Variant styles
      ...(chipVariant === 'filled' && {
        backgroundColor,
        color: textColor,
        border: 'none'
      }),

      ...(chipVariant === 'outlined' && {
        backgroundColor: 'transparent',
        color: backgroundColor,
        border: `1px solid ${backgroundColor}`,
        // Adjust padding to maintain visual balance with border
        paddingTop: 0,
        paddingBottom: 0
      })
    };
  }
);

// ==============================|| UI - CHIP ||============================== //

export interface UIChipProps extends Omit<MuiChipProps, 'variant' | 'color' | 'size' | 'avatar' | 'icon'> {
  /** Chip variant type */
  variant?: ChipVariant;
  /** Chip color from theme palette */
  color?: ColorProps;
  /** Custom background color (overrides theme color) */
  customColor?: string;
  /** Custom text color */
  customTextColor?: string;
  /** Custom font size (e.g., '0.75rem', '12px', 14) */
  fontSize?: string | number;
  /** Custom font weight (e.g., 400, 500, 600, 'bold') */
  fontWeight?: string | number;
  /** Chip size */
  size?: ChipSize;
  /** Chip label/text */
  label: ReactNode;
  /** Icon to display at the start */
  icon?: ReactNode;
  /** Icon to display at the end (avatar side) */
  avatar?: ReactNode;
  /** Delete icon */
  onDelete?: () => void;
  /** Click handler */
  onClick?: (event: React.MouseEvent<HTMLDivElement>) => void;
  /** Disabled state */
  disabled?: boolean;
  /** Additional sx props */
  sx?: SxProps<Theme>;
}

const Chip = forwardRef(
  (
    {
      variant = 'filled',
      color = 'primary',
      customColor,
      customTextColor,
      fontSize,
      fontWeight,
      size = 'medium',
      label,
      icon,
      avatar,
      onDelete,
      onClick,
      disabled = false,
      sx,
      ...others
    }: UIChipProps,
    ref: Ref<HTMLDivElement>
  ) => {
    const theme = useTheme();

    return (
      <StyledChip
        ref={ref}
        label={label}
        icon={icon as React.ReactElement<any>}
        avatar={avatar as React.ReactElement<any>}
        onDelete={onDelete}
        onClick={onClick}
        disabled={disabled}
        theme={theme}
        chipVariant={variant}
        chipColor={color}
        customColor={customColor}
        customTextColor={customTextColor}
        customFontSize={fontSize}
        customFontWeight={fontWeight}
        chipSize={size}
        sx={sx}
        {...others}
      />
    );
  }
);

Chip.displayName = 'Chip';

export default Chip;
